/* CategorySlugRouter - Disambiguates /:category/:slug URLs
 * Created: 2026-08-03 - Resolves whether the second path segment is a subcategory or a product
 * Checks product_subcategories first, then falls back to ProductPage
 * Updated: 2026-08-03 - Pass resolved IDs to SubcategoryPage to avoid duplicate lookups
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import SubcategoryPage from '@/pages/SubcategoryPage';
import ProductPage from '@/pages/ProductPage';
import { Loader as Loader2 } from 'lucide-react';
import ContactBar from '@/components/ContactBar';

const KNOWN_CATEGORIES = ['haagplanten', 'bomen', 'tuinplanten'];

interface ResolvedSubcategory {
  categoryId: string;
  subcategoryId: string;
  subcategoryName: string;
}

export default function CategorySlugRouter() {
  const { category, product: subcategory } = useParams<{ category: string; product: string }>();
  const [resolved, setResolved] = useState<'subcategory' | 'product' | null>(null);
  const [resolvedData, setResolvedData] = useState<ResolvedSubcategory | null>(null);

  useEffect(() => {
    const resolve = async () => {
      if (!category || !subcategory) {
        setResolved('product');
        return;
      }

      if (!KNOWN_CATEGORIES.includes(category)) {
        setResolved('product');
        return;
      }

      try {
        const { data: catData } = await supabase
          .from('product_categories')
          .select('id')
          .eq('slug', category)
          .maybeSingle();

        if (catData) {
          const { data: subData } = await supabase
            .from('product_subcategories')
            .select('id, name')
            .eq('slug', subcategory)
            .eq('category_id', catData.id)
            .maybeSingle();

          if (subData) {
            setResolvedData({
              categoryId: catData.id,
              subcategoryId: subData.id,
              subcategoryName: subData.name,
            });
            setResolved('subcategory');
            return;
          }
        }
      } catch {
        // fall through to product
      }

      setResolved('product');
    };

    resolve();
  }, [category, subcategory]);

  if (resolved === null) {
    return (
      <>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-white pt-0">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
      </>
    );
  }

  if (resolved === 'subcategory' && resolvedData) {
    return <SubcategoryPage {...resolvedData} />;
  }

  return <ProductPage />;
}
